// API do acervo de documentos da empresa.
//
// A rota continua sendo `POST /comercial/documentos` mesmo agora que a tela
// mora no Administrativo: o recurso já existe no API Gateway, e criar rota nova
// lá é um ritual manual (recurso + método + integração + OPTIONS + permissão +
// deploy do stage) cujo esquecimento vira 403 silencioso. O nome da rota é
// transporte; o menu é o que o usuário vê.
//
// Quem pode o quê está no backend (handlers/documentos.js): a regra é pelo
// ESCOPO do documento — com escopo_oportunidade_id é do comercial (declaração
// de edital), sem escopo é do acervo da empresa (administrativo).
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import type { CategoriaDoc, SubcategoriaDoc, Documento, TipoVinculo, Esperado, ResumoChecklist } from './types';

async function chamar<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/documentos`,
    { action, ...body }, { headers: { Authorization: token } });
  // A API às vezes devolve envelopada em `body` (stringificada), às vezes não.
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export type FiltrosDoc = {
  categoria_codigo?: string; subcategoria_codigo?: string; status?: string; q?: string;
  escopo_oportunidade_id?: number;
  /** Arquivado sai da lista por padrão; isto traz de volta para consulta. */
  incluir_arquivados?: boolean;
};

export const listarDocumentos   = (f: FiltrosDoc = {}) => chamar<{ documentos: Documento[] }>('list', f);
export const obterDocumento     = (id: number, comDownload = false) =>
  chamar<{ documento: Documento; download_url?: string }>('get', { id, com_download: comDownload });
export const listarCategorias    = () => chamar<{ categorias: CategoriaDoc[] }>('categorias');
export const listarSubcategorias = (categoria_codigo?: string) =>
  chamar<{ subcategorias: SubcategoriaDoc[] }>('subcategorias', categoria_codigo ? { categoria_codigo } : {});

export const pedirUploadUrl = (p: { filename: string; content_type: string; categoria_codigo: string }) =>
  chamar<{ upload_url: string; s3_key: string }>('upload_url', p);
export const criarDocumento     = (p: Record<string, any>) => chamar<{ documento: Documento; sumarizacao_ok?: boolean }>('criar', p);
export const atualizarDocumento = (p: Record<string, any>) => chamar<{ documento: Documento }>('atualizar', p);
export const deletarDocumento   = (id: number) => chamar<{ deletado: boolean }>('deletar', { id });
/** Tira o documento do que cobra (checklist, aviso semanal, lista padrão) sem
 *  apagar nada. Para papel SUBSTITUÍDO — a apólice de 2025 quando a de 2026 já
 *  está no acervo. `substituido_por_id` responde "e qual é o que vale?". */
export const arquivarDocumento = (p: { id: number; arquivado?: boolean; motivo?: string; substituido_por_id?: number | null }) =>
  chamar<{ documento: Documento; arquivado: boolean }>('arquivar', p);

// Checklist do que a empresa deve manter. `resumo` vem do backend contando o
// checklist INTEIRO — filtrar por situação na tela não pode mudar o placar.
export const listarEsperados = (f: { categoria_codigo?: string; situacao?: string } = {}) =>
  chamar<{ esperados: Esperado[]; resumo: ResumoChecklist; count: number }>('esperados', f);
export const salvarEsperado  = (p: Record<string, any>) =>
  chamar<{ esperado: { id: number; codigo: string }; criado: boolean }>('esperado_salvar', p);
export const excluirEsperado = (id: number) => chamar<{ excluido: boolean }>('esperado_excluir', { id });

export const vincularDocs   = (doc_a_id: number, doc_b_id: number, tipo_vinculo?: TipoVinculo | null, observacoes?: string) =>
  chamar('vincular', { doc_a_id, doc_b_id, tipo_vinculo, observacoes });
export const desvincularDocs = (doc_a_id: number, doc_b_id: number) =>
  chamar('desvincular', { doc_a_id, doc_b_id });

// Sobe o arquivo direto no S3 pela URL assinada (o PUT não passa pela Lambda).
export async function subirArquivo(arquivo: File, categoria_codigo: string) {
  const { upload_url, s3_key } = await pedirUploadUrl({
    filename: arquivo.name,
    content_type: arquivo.type || 'application/octet-stream',
    categoria_codigo,
  });
  await axios.put(upload_url, arquivo, {
    headers: { 'Content-Type': arquivo.type || 'application/octet-stream' },
  });
  return s3_key;
}
